/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Save/Load Data Validation Utilities
 */

import { Grid, CityStats, ResearchState, BuildingType, TileData } from '../types';
import { GRID_SIZE } from '../constants';
import { createInitialGrid } from '../src/domain/services/gridFactory';

export const DEFAULT_SAVE_VERSION = '3.0.0';

export interface SaveData {
  version: string;
  timestamp: number;
  grid: Grid;
  stats: CityStats;
  research: ResearchState;
  gameStatus: string;
  newsFeed: any[];
  currentGoal: any;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface SaveLoadRecoveryResult {
  data: SaveData;
  validation: ValidationResult;
  recovered: boolean;
  /** User-visible explanation when save was repaired or replaced with defaults */
  recoveryReason: string | null;
}

const DEFAULT_STATS = (): CityStats => ({
  budget: 1500,
  compliance: 65,
  threat: 0,
  threatHistory: Array(20).fill(0),
  day: 1,
  loanCount: 0,
  lastPitchDay: -10,
  activeProducts: [],
});

const DEFAULT_RESEARCH = (): ResearchState => ({
  unlockedBuildings: [],
  completedTechs: [],
  activeResearchId: null,
  researchProgress: 0,
  currentMaxLevel: 1,
});

/**
 * Creates a fully valid default save payload for corrupt-save fallback.
 */
export const createDefaultSaveData = (): SaveData => ({
  version: DEFAULT_SAVE_VERSION,
  timestamp: Date.now(),
  grid: createInitialGrid(),
  stats: DEFAULT_STATS(),
  research: DEFAULT_RESEARCH(),
  gameStatus: 'playing',
  newsFeed: [],
  currentGoal: null,
});

/**
 * Validates a tile data object
 */
const validateTile = (tile: any, x: number, y: number): string[] => {
  const errors: string[] = [];

  if (typeof tile !== 'object' || tile === null) {
    errors.push(`Tile at (${x}, ${y}) is not a valid object`);
    return errors;
  }

  if (typeof tile.x !== 'number' || tile.x !== x) {
    errors.push(`Tile at (${x}, ${y}) has invalid x coordinate`);
  }

  if (typeof tile.y !== 'number' || tile.y !== y) {
    errors.push(`Tile at (${x}, ${y}) has invalid y coordinate`);
  }

  if (!Object.values(BuildingType).includes(tile.buildingType)) {
    errors.push(`Tile at (${x}, ${y}) has invalid building type: ${tile.buildingType}`);
  }

  if (typeof tile.level !== 'number' || tile.level < 1 || !Number.isFinite(tile.level)) {
    errors.push(`Tile at (${x}, ${y}) has invalid level: ${tile.level}`);
  }

  if (tile.variant !== undefined && (typeof tile.variant !== 'number' || !Number.isFinite(tile.variant))) {
    errors.push(`Tile at (${x}, ${y}) has invalid variant`);
  }

  if (tile.isConnected !== undefined && typeof tile.isConnected !== 'boolean') {
    errors.push(`Tile at (${x}, ${y}) has invalid isConnected flag`);
  }

  return errors;
};

/**
 * Validates the grid structure and dimensions
 */
const validateGrid = (grid: any): string[] => {
  const errors: string[] = [];

  if (!Array.isArray(grid)) {
    errors.push('Grid is not an array');
    return errors;
  }

  if (grid.length !== GRID_SIZE) {
    errors.push(`Grid has ${grid.length} rows, expected ${GRID_SIZE}`);
  }

  for (let y = 0; y < grid.length; y++) {
    if (!Array.isArray(grid[y])) {
      errors.push(`Grid row ${y} is not an array`);
      continue;
    }

    if (grid[y].length !== GRID_SIZE) {
      errors.push(`Grid row ${y} has ${grid[y].length} columns, expected ${GRID_SIZE}`);
    }

    for (let x = 0; x < grid[y].length; x++) {
      errors.push(...validateTile(grid[y][x], x, y));
    }
  }

  return errors;
};

/**
 * Validates city stats constraints
 */
const validateStats = (stats: any): string[] => {
  const errors: string[] = [];

  if (typeof stats !== 'object' || stats === null) {
    errors.push('Stats is not a valid object');
    return errors;
  }

  if (typeof stats.budget !== 'number' || !Number.isFinite(stats.budget) || stats.budget < 0) {
    errors.push('Stats budget must be a number >= 0');
  }

  if (
    typeof stats.compliance !== 'number' ||
    !Number.isFinite(stats.compliance) ||
    stats.compliance < 0 ||
    stats.compliance > 100
  ) {
    errors.push('Stats compliance must be between 0 and 100');
  }

  if (
    typeof stats.threat !== 'number' ||
    !Number.isFinite(stats.threat) ||
    stats.threat < 0 ||
    stats.threat > 100
  ) {
    errors.push('Stats threat must be between 0 and 100');
  }

  if (typeof stats.day !== 'number' || !Number.isFinite(stats.day) || stats.day < 1) {
    errors.push('Stats day must be a number >= 1');
  }

  if (stats.loanCount !== undefined) {
    if (typeof stats.loanCount !== 'number' || !Number.isFinite(stats.loanCount) || stats.loanCount < 0) {
      errors.push('Stats loanCount must be a number >= 0');
    }
  }

  if (stats.lastPitchDay !== undefined) {
    if (typeof stats.lastPitchDay !== 'number' || !Number.isFinite(stats.lastPitchDay)) {
      errors.push('Stats lastPitchDay must be a number');
    }
  }

  if (!Array.isArray(stats.activeProducts)) {
    errors.push('Stats activeProducts is not an array');
  }

  if (!Array.isArray(stats.threatHistory)) {
    errors.push('Stats threatHistory is not an array');
  } else if (stats.threatHistory.some((v: unknown) => typeof v !== 'number' || !Number.isFinite(v))) {
    errors.push('Stats threatHistory contains non-numeric values');
  }

  return errors;
};

/**
 * Validates research state
 */
const validateResearch = (research: any): string[] => {
  const errors: string[] = [];

  if (typeof research !== 'object' || research === null) {
    errors.push('Research is not a valid object');
    return errors;
  }

  if (!Array.isArray(research.unlockedBuildings)) {
    errors.push('Research unlockedBuildings is not an array');
  }

  if (!Array.isArray(research.completedTechs)) {
    errors.push('Research completedTechs is not an array');
  }

  const progress = research.researchProgress;
  if (typeof progress !== 'number' || !Number.isFinite(progress) || progress < 0) {
    errors.push('Research progress is invalid');
  } else if (progress > 1 && progress > 100) {
    errors.push('Research progress is invalid (should be 0-1 or 0-100)');
  }

  if (
    typeof research.currentMaxLevel !== 'number' ||
    !Number.isFinite(research.currentMaxLevel) ||
    research.currentMaxLevel < 1
  ) {
    errors.push('Research currentMaxLevel is invalid');
  }

  if (
    research.activeResearchId !== null &&
    research.activeResearchId !== undefined &&
    typeof research.activeResearchId !== 'string'
  ) {
    errors.push('Research activeResearchId must be a string or null');
  }

  return errors;
};

const normalizeResearchProgress = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  if (value <= 1) {
    return Math.max(0, Math.min(100, value * 100));
  }
  return Math.max(0, Math.min(100, value));
};

const repairGrid = (grid: any): Grid => {
  const repaired: Grid = [];
  for (let y = 0; y < GRID_SIZE; y++) {
    const row: TileData[] = [];
    for (let x = 0; x < GRID_SIZE; x++) {
      const source = Array.isArray(grid?.[y]) ? grid[y][x] : undefined;
      row.push({
        x,
        y,
        buildingType: Object.values(BuildingType).includes(source?.buildingType)
          ? source.buildingType
          : BuildingType.None,
        level: typeof source?.level === 'number' && source.level >= 1 ? source.level : 1,
        isConnected: !!source?.isConnected,
        ...(typeof source?.variant === 'number' ? { variant: source.variant } : {}),
      });
    }
    repaired.push(row);
  }
  return repaired;
};

/**
 * Main validation function for save data
 */
export const validateSaveData = (data: any): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!data) {
    return { isValid: false, errors: ['Save data is empty or null'], warnings: [] };
  }

  if (!data.version) {
    warnings.push('Save data has no version, may be from older version');
  } else if (typeof data.version !== 'string') {
    errors.push('Save data version must be a string');
  }

  if (!data.timestamp || typeof data.timestamp !== 'number' || !Number.isFinite(data.timestamp)) {
    warnings.push('Save data has no valid timestamp');
  }

  if (data.grid) {
    errors.push(...validateGrid(data.grid));
  } else {
    errors.push('Save data has no grid');
  }

  if (data.stats) {
    errors.push(...validateStats(data.stats));
  } else {
    errors.push('Save data has no stats');
  }

  if (data.research) {
    errors.push(...validateResearch(data.research));
  } else {
    errors.push('Save data has no research state');
  }

  if (data.gameStatus !== undefined) {
    const validStatuses = ['playing', 'won', 'lost', 'scenario_won'];
    if (!validStatuses.includes(data.gameStatus)) {
      warnings.push(`Unknown gameStatus "${data.gameStatus}", will default to playing`);
    }
  }

  if (data.newsFeed !== undefined && !Array.isArray(data.newsFeed)) {
    warnings.push('newsFeed is not an array, will be reset');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Attempts to repair corrupted save data
 */
export const repairSaveData = (data: any): SaveData | null => {
  try {
    const repaired: Partial<SaveData> = {
      version: typeof data?.version === 'string' ? data.version : DEFAULT_SAVE_VERSION,
      timestamp:
        typeof data?.timestamp === 'number' && Number.isFinite(data.timestamp)
          ? data.timestamp
          : Date.now(),
    };

    repaired.grid = Array.isArray(data?.grid) ? repairGrid(data.grid) : createInitialGrid();

    if (data?.stats && typeof data.stats === 'object') {
      repaired.stats = {
        budget:
          typeof data.stats.budget === 'number' && Number.isFinite(data.stats.budget)
            ? Math.max(0, data.stats.budget)
            : DEFAULT_STATS().budget,
        compliance:
          typeof data.stats.compliance === 'number' && Number.isFinite(data.stats.compliance)
            ? Math.max(0, Math.min(100, data.stats.compliance))
            : DEFAULT_STATS().compliance,
        threat:
          typeof data.stats.threat === 'number' && Number.isFinite(data.stats.threat)
            ? Math.max(0, Math.min(100, data.stats.threat))
            : DEFAULT_STATS().threat,
        threatHistory: Array.isArray(data.stats.threatHistory)
          ? data.stats.threatHistory.map((v: unknown) =>
              typeof v === 'number' && Number.isFinite(v) ? v : 0,
            )
          : Array(20).fill(0),
        day:
          typeof data.stats.day === 'number' && data.stats.day >= 1
            ? data.stats.day
            : DEFAULT_STATS().day,
        loanCount:
          typeof data.stats.loanCount === 'number' && data.stats.loanCount >= 0
            ? data.stats.loanCount
            : 0,
        lastPitchDay:
          typeof data.stats.lastPitchDay === 'number'
            ? data.stats.lastPitchDay
            : DEFAULT_STATS().lastPitchDay,
        activeProducts: Array.isArray(data.stats.activeProducts) ? data.stats.activeProducts : [],
      };
    } else {
      repaired.stats = DEFAULT_STATS();
    }

    if (data?.research && typeof data.research === 'object') {
      repaired.research = {
        unlockedBuildings: Array.isArray(data.research.unlockedBuildings)
          ? data.research.unlockedBuildings.filter((b: unknown) =>
              Object.values(BuildingType).includes(b as BuildingType),
            )
          : [],
        completedTechs: Array.isArray(data.research.completedTechs)
          ? data.research.completedTechs.filter((t: unknown) => typeof t === 'string')
          : [],
        activeResearchId:
          typeof data.research.activeResearchId === 'string'
            ? data.research.activeResearchId
            : null,
        researchProgress: normalizeResearchProgress(data.research.researchProgress),
        currentMaxLevel:
          typeof data.research.currentMaxLevel === 'number' && data.research.currentMaxLevel >= 1
            ? data.research.currentMaxLevel
            : 1,
      };
    } else {
      repaired.research = DEFAULT_RESEARCH();
    }

    const validStatuses = ['playing', 'won', 'lost', 'scenario_won'];
    repaired.gameStatus =
      typeof data?.gameStatus === 'string' && validStatuses.includes(data.gameStatus)
        ? data.gameStatus
        : 'playing';
    repaired.newsFeed = Array.isArray(data?.newsFeed) ? data.newsFeed : [];
    repaired.currentGoal = data?.currentGoal ?? null;

    const postRepairValidation = validateSaveData(repaired);
    if (!postRepairValidation.isValid) {
      return null;
    }

    return repaired as SaveData;
  } catch (e) {
    console.error('Failed to repair save data:', e);
    return null;
  }
};

const summarizeErrors = (errors: string[], max = 3): string =>
  errors.slice(0, max).join('; ');

/**
 * Validates save data and returns safe defaults with a user-visible reason when corrupt.
 */
export const recoverSaveData = (raw: unknown): SaveLoadRecoveryResult => {
  if (!raw || typeof raw !== 'object') {
    const defaults = createDefaultSaveData();
    return {
      data: defaults,
      validation: {
        isValid: false,
        errors: ['Save data is empty or null'],
        warnings: [],
      },
      recovered: true,
      recoveryReason:
        'Save file was missing or unreadable. Restored default city state.',
    };
  }

  const validation = validateSaveData(raw);
  if (validation.isValid) {
    const record = raw as Record<string, unknown>;
    const data = raw as SaveData;
    return {
      data: {
        ...data,
        version:
          typeof record.version === 'string' ? record.version : DEFAULT_SAVE_VERSION,
        timestamp:
          typeof record.timestamp === 'number' && Number.isFinite(record.timestamp)
            ? record.timestamp
            : Date.now(),
      },
      validation,
      recovered: false,
      recoveryReason: null,
    };
  }

  const repaired = repairSaveData(raw);
  if (repaired) {
    return {
      data: repaired,
      validation: validateSaveData(repaired),
      recovered: true,
      recoveryReason: `Save file was repaired: ${summarizeErrors(validation.errors)}`,
    };
  }

  const defaults = createDefaultSaveData();
  return {
    data: defaults,
    validation,
    recovered: true,
    recoveryReason: `Save file was corrupted (${summarizeErrors(validation.errors)}). Restored default city state.`,
  };
};

/**
 * Creates a backup of save data before loading
 */
export const createSaveBackup = (key: string): void => {
  try {
    const data = localStorage.getItem(key);
    if (data) {
      localStorage.setItem(`${key}_backup_${Date.now()}`, data);

      const backupKeys = Object.keys(localStorage)
        .filter((k) => k.startsWith(`${key}_backup_`))
        .sort()
        .reverse();

      backupKeys.slice(3).forEach((k) => localStorage.removeItem(k));
    }
  } catch (e) {
    console.warn('Failed to create save backup:', e);
  }
};